import "./styles.scss";

export interface JourneyItem {
  id: string;
  label: string;
  completed: boolean;
}

export interface JourneyPhase {
  id: string;
  title: string;
  items: JourneyItem[];
  color: string;
}

export interface JourneyMapProps {
  phases: JourneyPhase[];
  progress: number;
  onPhaseItemClick?: (itemId: string) => void;
}

const CheckMark = ({
  width = 20,
  height = 20,
}: {
  width?: number;
  height?: number;
}) => {
  return (
    <svg width={width} height={height} viewBox="0 0 20 20" fill="none">
      <path
        d="M16.6667 5L7.50004 14.1667L3.33337 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const Timeline = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="91"
      height="432"
      viewBox="0 0 91 432"
      fill="none"
    >
      <path
        d="M2.99658 3C2.99658 24.4392 2.99658 72.7083 2.99658 137L87.9966 156C87.9966 186.22 87.9966 233.975 87.9966 264.5L2.99658 290.531V394.5L59 428.5"
        stroke="white"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <path
        d="M2.99658 3C2.99658 24.4392 2.99658 72.7083 2.99658 137L87.9966 156C87.9966 186.22 87.9966 233.975 87.9966 264.5L2.99658 290.531V394.5L59 428.5"
        stroke="url(#paint0_linear_5501_83433)"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <defs>
        <linearGradient
          id="paint0_linear_5501_83433"
          x1="91.5852"
          y1="3"
          x2="91.5852"
          y2="436"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#00EBC8" />
          <stop offset="0.177877" stopColor="#00EBC8" />
          <stop offset="0.903857" stopColor="#B400FF" />
        </linearGradient>
      </defs>
    </svg>
  );
};

export function JourneyMap({
  phases,
  progress,
  onPhaseItemClick,
}: JourneyMapProps) {
  function renderItems(items: JourneyItem[], color: string) {
    return (
      <div className="phase-items">
        {items.map(item => (
          <div
            key={item.id}
            className={`phase-item ${item.completed ? "completed" : "pending"}`}
            onClick={() => onPhaseItemClick?.(item.id)}
            style={{ color }}
          >
            <span className="phase-item-icon">
              {item.completed ? (
                <CheckMark />
              ) : (
                <span className="question-mark">?</span>
              )}
            </span>
            <span className="phase-item-label" title={item.label}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    );
  }

  function renderPhase(phase: JourneyPhase, position: "left" | "right") {
    const phaseLabel = (
      <div className="phase-label" style={{ color: phase.color }}>
        {phase.title}
      </div>
    );

    const phaseItems = renderItems(phase.items, phase.color);

    if (position === "left") {
      return (
        <>
          {phaseItems}
          {phaseLabel}
        </>
      );
    }

    return (
      <>
        {phaseLabel}
        {phaseItems}
      </>
    );
  }

  return (
    <div className="journey-map">
      <div className="journey-container">
        {phases.map((phase, phaseIndex) => {
          const position = phaseIndex % 2 === 0 ? "left" : "right";

          return (
            <div key={phase.id} className={`journey-phase items-${position}`}>
              {renderPhase(phase, position)}
            </div>
          );
        })}

        <div className="journey-timeline">
          <Timeline />
          <div className="progress-circle">
            <div className="progress-inner">
              {progress === 100 ? (
                <CheckMark width={24} height={24} />
              ) : (
                `${Math.round(progress)}%`
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
